import { Locales } from '@griply/types';
import { LocalizedText } from '@griply/utils';
import imagePage2 from 'shared/assets/images/whats-new/calendar-events.jpg';
import imagePage2x2 from 'shared/assets/images/whats-new/calendar-events@2x.jpg';
import imagePage2x3 from 'shared/assets/images/whats-new/calendar-events@3x.jpg';
import imagePage3 from 'shared/assets/images/whats-new/create-focus-blocks.jpg';
import imagePage3x2 from 'shared/assets/images/whats-new/create-focus-blocks@2x.jpg';
import imagePage3x3 from 'shared/assets/images/whats-new/create-focus-blocks@3x.jpg';
import imagePage4 from 'shared/assets/images/whats-new/reminders.jpg';
import imagePage4x2 from 'shared/assets/images/whats-new/reminders@2x.jpg';
import imagePage4x3 from 'shared/assets/images/whats-new/reminders@3x.jpg';
import imagePage1 from 'shared/assets/images/whats-new/two-way-sync.jpg';
import imagePage1x2 from 'shared/assets/images/whats-new/two-way-sync@2x.jpg';
import imagePage1x3 from 'shared/assets/images/whats-new/two-way-sync@3x.jpg';
// Deliberate mobile parallel — `image`/`imageSrcSet` are platform-divergent and the content is not shared.
export type WhatsNewBullet = {
  title: LocalizedText;
  description?: LocalizedText;
};

export type WhatsNewPage = {
  image?: string;
  imageSrcSet?: string;
  newBadge?: boolean;
  title: LocalizedText;
  subtitle?: LocalizedText;
  bullets: WhatsNewBullet[];
  link?: { url: string; title: LocalizedText };
};

export type WhatsNewContent = {
  version: string;
  pages: WhatsNewPage[];
};

// The dialog is keyed to `version`, NOT the app's build version. Each user is
// shown a given `version` exactly once (tracked in the profile). Rules:
//
// - New notes to show      → bump `version` AND replace the pages.
// - Same notes, new build   → leave `version` (hot fix, patch, phased reship).
//   This is how patch releases (3.xx.1 / 3.xx.2) are skipped: leave `version`.
// - No dialog at all        → set the whole value to `null`.
//
// Bumping `version` re-shows the dialog to everyone who already saw the old one;
// leaving it still reaches anyone who skipped the prior build.
export const WHATS_NEW_CONTENT: WhatsNewContent | null = {
  version: '3.52.0',
  pages: [
    {
      image: imagePage1,
      imageSrcSet: `${imagePage1} 1x, ${imagePage1x2} 2x, ${imagePage1x3} 3x`,
      newBadge: true,
      title: {
        [Locales.EnUS]: 'Two-way calendar sync (Beta)',
        [Locales.NlNL]: 'Agenda in twee richtingen (Beta)',
        [Locales.DeDE]: 'Zwei-Wege-Kalendersync (Beta)',
        [Locales.EsES]: 'Calendario bidireccional (Beta)',
      },
      subtitle: {
        [Locales.EnUS]: 'Sync your tasks and habits to your calendar and back.',
        [Locales.NlNL]:
          'Verzet een taak in je agenda en Griply werkt zichzelf bij.',
        [Locales.DeDE]:
          'Verschieb eine Aufgabe im Kalender und Griply passt sich an.',
        [Locales.EsES]:
          'Mueve una tarea en tu calendario y Griply se actualiza para reflejarlo.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Full control',
            [Locales.NlNL]: 'Wat waarheen',
            [Locales.DeDE]: 'Was wohin',
            [Locales.EsES]: 'Qué se sincroniza',
          },
          description: {
            [Locales.EnUS]:
              'Sync all tasks, a life area or a goal, each to its own calendar. ⚠️ Reconnect your calendar first.',
            [Locales.NlNL]:
              'Sync alle taken, een levensgebied of een doel, elk naar een eigen agenda. ⚠️ Koppel je agenda eerst opnieuw.',
            [Locales.DeDE]:
              'Sync alle Aufgaben, einen Lebensbereich oder ein Ziel in je einen Kalender. ⚠️ Kalender neu verbinden.',
            [Locales.EsES]:
              'Manda todas las tareas, un área de vida o un objetivo a su propio calendario. ⚠️ Reconecta tu calendario.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Auto or manual',
            [Locales.NlNL]: 'Automatisch of zelf',
            [Locales.DeDE]: 'Auto oder manuell',
            [Locales.EsES]: 'Automático o manual',
          },
          description: {
            [Locales.EnUS]:
              'Sync everything automatically, or block specific tasks in your calendar.',
            [Locales.NlNL]:
              'Sync alles automatisch, of stuur taken één voor één met het agenda-icoon.',
            [Locales.DeDE]:
              'Sync alles automatisch, oder schick Aufgaben einzeln über das Kalendersymbol.',
            [Locales.EsES]:
              'Sincroniza todo automáticamente, o manda tareas una a una con el icono de calendario.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Privacy',
            [Locales.NlNL]: 'Privacy',
            [Locales.DeDE]: 'Privatsphäre',
            [Locales.EsES]: 'Privacidad',
          },
          description: {
            [Locales.EnUS]:
              'Show the task name to anyone sharing your calendar, or hide it so the block reads "Busy".',
            [Locales.NlNL]:
              'Laat iedereen die je agenda ziet de taaknaam lezen, of verberg hem en toon "Bezet".',
            [Locales.DeDE]:
              'Zeig allen, die deinen Kalender sehen, den Aufgabennamen, oder verbirg ihn als "Beschäftigt".',
            [Locales.EsES]:
              'Deja ver el nombre de la tarea a quien comparte tu calendario, u ocúltalo y que se lea "Ocupado".',
          },
        },
      ],
      link: {
        url: 'https://griply.app/help/integrations/calendar-integration',
        title: { [Locales.EnUS]: 'Learn more' },
      },
    },
    {
      image: imagePage2,
      imageSrcSet: `${imagePage2} 1x, ${imagePage2x2} 2x, ${imagePage2x3} 3x`,
      newBadge: true,
      title: {
        [Locales.EnUS]: 'Manage calendar events in Griply (Beta)',
        [Locales.NlNL]: 'Beheer agenda-items in Griply (Beta)',
        [Locales.DeDE]: 'Termine in Griply verwalten (Beta)',
        [Locales.EsES]: 'Gestiona eventos de calendario en Griply (Beta)',
      },
      subtitle: {
        [Locales.EnUS]:
          'Reschedule, add guests or reply to an invite where you plan your day.',
        [Locales.NlNL]:
          'Verzet afspraken, nodig gasten uit of reageer op uitnodigingen waar je plant.',
        [Locales.DeDE]:
          'Verschieb Termine, lad Gäste ein oder antworte auf Einladungen beim Planen.',
        [Locales.EsES]:
          'Reprograma, añade invitados o responde a una invitación donde planificas tu día.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Edit in place',
            [Locales.NlNL]: 'Direct bewerken',
            [Locales.DeDE]: 'Direkt bearbeiten',
            [Locales.EsES]: 'Edición directa',
          },
          description: {
            [Locales.EnUS]:
              'Move a meeting to another time, or fix its title and description, without leaving Griply.',
            [Locales.NlNL]:
              'Verzet een afspraak, of pas de titel en omschrijving aan, zonder Griply te verlaten.',
            [Locales.DeDE]:
              'Leg einen Termin auf eine andere Zeit oder korrigier Titel und Beschreibung, ohne Griply zu verlassen.',
            [Locales.EsES]:
              'Cambia la hora de una reunión, o corrige su título y descripción, sin salir de Griply.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Guests',
            [Locales.NlNL]: 'Gasten',
            [Locales.DeDE]: 'Gäste',
            [Locales.EsES]: 'Invitados',
          },
          description: {
            [Locales.EnUS]:
              'Create an event, pick guests from your contacts and drop in a Google Meet link.',
            [Locales.NlNL]:
              'Maak een afspraak, kies gasten uit je contacten en voeg een Google Meet-link toe.',
            [Locales.DeDE]:
              'Erstell einen Termin mit Gästen aus deinen Kontakten und einem Google Meet-Link.',
            [Locales.EsES]:
              'Crea un evento, elige invitados de tus contactos y añade un enlace de Google Meet.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Invitations',
            [Locales.NlNL]: 'Uitnodigingen',
            [Locales.DeDE]: 'Einladungen',
            [Locales.EsES]: 'Invitaciones',
          },
          description: {
            [Locales.EnUS]:
              'Answer a meeting invite with "Yes", "No" or "Maybe" without opening Google or Outlook.',
            [Locales.NlNL]:
              'Beantwoord een uitnodiging met "Ja", "Nee" of "Misschien" zonder Google of Outlook te openen.',
            [Locales.DeDE]:
              'Antworte auf eine Einladung mit "Ja", "Nein" oder "Vielleicht", ohne Google oder Outlook zu öffnen.',
            [Locales.EsES]:
              'Responde a una invitación con "Sí", "No" o "Quizá" sin abrir Google ni Outlook.',
          },
        },
      ],
    },
    {
      image: imagePage3,
      imageSrcSet: `${imagePage3} 1x, ${imagePage3x2} 2x, ${imagePage3x3} 3x`,
      newBadge: true,
      title: {
        [Locales.EnUS]: 'Focus blocks on your calendar',
        [Locales.NlNL]: 'Focusblokken in je agenda',
        [Locales.DeDE]: 'Fokusblöcke im Kalender',
        [Locales.EsES]: 'Bloques de enfoque en tu calendario',
      },
      subtitle: {
        [Locales.EnUS]:
          'Turn an empty hour into a task, an event or focused work.',
        [Locales.NlNL]:
          'Maak van een leeg uur een taak, een afspraak of tijd om te focussen.',
        [Locales.DeDE]:
          'Mach aus einer leeren Stunde eine Aufgabe, einen Termin oder Fokuszeit.',
        [Locales.EsES]:
          'Convierte una hora libre en una tarea, un evento o trabajo concentrado.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Drag and pick',
            [Locales.NlNL]: 'Slepen en kiezen',
            [Locales.DeDE]: 'Ziehen und wählen',
            [Locales.EsES]: 'Arrastra y elige',
          },
          description: {
            [Locales.EnUS]:
              'Drag a slot and pick a task, event or focus block with keys 1 to 5, or hold Alt to immediately create a task.',
            [Locales.NlNL]:
              'Sleep een tijdvak open en kies met 1 tot 5 een taak, afspraak of focusblok, of houd Alt vast om meteen een taak te maken.',
            [Locales.DeDE]:
              'Zieh einen Zeitraum auf, wähl mit 1 bis 5 Aufgabe, Termin oder Fokusblock, oder halt Alt für eine sofortige Aufgabe.',
            [Locales.EsES]:
              'Arrastra un hueco y elige tarea, evento o bloque con las teclas 1 a 5, o mantén Alt para crear una tarea al instante.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Tags',
            [Locales.NlNL]: 'Labels',
            [Locales.DeDE]: 'Tags',
            [Locales.EsES]: 'Etiquetas',
          },
          description: {
            [Locales.EnUS]:
              'Block time for a dedicated tag to focus on #deep-work.',
            [Locales.NlNL]: 'Blok tijd voor één label en focus op #deep-work.',
            [Locales.DeDE]:
              'Blockier Zeit für einen bestimmten Tag und fokussier dich auf #deep-work.',
            [Locales.EsES]:
              'Bloquea tiempo para una etiqueta concreta y céntrate en #deep-work.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Smart lists',
            [Locales.NlNL]: 'Slimme lijsten',
            [Locales.DeDE]: 'Smarte Listen',
            [Locales.EsES]: 'Listas inteligentes',
          },
          description: {
            [Locales.EnUS]:
              'Block time for a smart list to focus on tasks from planned goals first.',
            [Locales.NlNL]:
              'Blok tijd voor een slimme lijst en werk eerst aan taken uit geplande doelen.',
            [Locales.DeDE]:
              'Blockier Zeit für eine smarte Liste und arbeite zuerst an Aufgaben aus geplanten Zielen.',
            [Locales.EsES]:
              'Bloquea tiempo para una lista inteligente y ve primero a las tareas de objetivos planificados.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Duplicate',
            [Locales.NlNL]: 'Dupliceren',
            [Locales.DeDE]: 'Duplizieren',
            [Locales.EsES]: 'Duplicar',
          },
          description: {
            [Locales.EnUS]: 'Right-click a focus block to duplicate it.',
            [Locales.NlNL]:
              'Klik rechts op een focusblok om het te dupliceren.',
            [Locales.DeDE]: 'Rechtsklick auf einen Fokusblock dupliziert ihn.',
            [Locales.EsES]:
              'Haz clic derecho en un bloque de enfoque para duplicarlo.',
          },
        },
      ],
    },
    {
      image: imagePage4,
      imageSrcSet: `${imagePage4} 1x, ${imagePage4x2} 2x, ${imagePage4x3} 3x`,
      newBadge: true,
      title: {
        [Locales.EnUS]: 'Reminders that fit your day',
        [Locales.NlNL]: 'Herinneringen op jouw manier',
        [Locales.DeDE]: 'Erinnerungen für deinen Tag',
        [Locales.EsES]: 'Recordatorios a tu medida',
      },
      subtitle: {
        [Locales.EnUS]:
          'Set your defaults once, then add as many reminders as a task needs.',
        [Locales.NlNL]:
          'Stel je standaarden één keer in en voeg per taak zoveel herinneringen toe als nodig.',
        [Locales.DeDE]:
          'Leg die Standards einmal fest, dann so viele Erinnerungen wie die Aufgabe braucht.',
        [Locales.EsES]:
          'Configúralos una vez y añade los recordatorios que necesite cada tarea.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Defaults',
            [Locales.NlNL]: 'Standaarden',
            [Locales.DeDE]: 'Standards',
            [Locales.EsES]: 'Por defecto',
          },
          description: {
            [Locales.EnUS]:
              'Set defaults for timed and all-day tasks in Settings > Notifications, and every task follows.',
            [Locales.NlNL]:
              'Stel er één in voor taken met tijd en één voor taken van de hele dag bij Instellingen > Meldingen.',
            [Locales.DeDE]:
              'Setz je einen Standard für Aufgaben mit und ohne Uhrzeit unter Einstellungen > Benachrichtigungen.',
            [Locales.EsES]:
              'Define uno para tareas con hora y otro para las de todo el día en Ajustes > Notificaciones.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Multiple reminders',
            [Locales.NlNL]: 'Meerdere herinneringen',
            [Locales.DeDE]: 'Mehrere Erinnerungen',
            [Locales.EsES]: 'Varios recordatorios',
          },
          description: {
            [Locales.EnUS]: 'Set multiple reminders per task or habit.',
            [Locales.NlNL]:
              'Zet meerdere herinneringen op één taak of gewoonte.',
            [Locales.DeDE]:
              'Setz mehrere Erinnerungen pro Aufgabe oder Gewohnheit.',
            [Locales.EsES]:
              'Pon varios recordatorios en una misma tarea o hábito.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'At time of task',
            [Locales.NlNL]: 'Op tijdstip van taak',
            [Locales.DeDE]: 'Zum Zeitpunkt der Aufgabe',
            [Locales.EsES]: 'A la hora de la tarea',
          },
          description: {
            [Locales.EnUS]:
              'Choose "At time of task" and the reminder fires the moment the task starts.',
            [Locales.NlNL]:
              'Kies "Op tijdstip van taak" en het seintje komt precies wanneer de taak begint.',
            [Locales.DeDE]:
              'Wähl "Zum Zeitpunkt der Aufgabe" und die Erinnerung kommt genau beim Start der Aufgabe.',
            [Locales.EsES]:
              'Elige "A la hora de la tarea" y el aviso llega justo cuando la tarea empieza.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Quick entry',
            [Locales.NlNL]: 'Snel invoeren',
            [Locales.DeDE]: 'Schnelleingabe',
            [Locales.EsES]: 'Entrada rápida',
          },
          description: {
            [Locales.EnUS]:
              'Type !10m or !1h30m into a task name while you create it, to set multiple reminders.',
            [Locales.NlNL]:
              'Typ !10m of !1h30m in de taaknaam terwijl je hem maakt, voor meerdere herinneringen.',
            [Locales.DeDE]:
              'Tipp !10m oder !1h30m in den Aufgabennamen beim Anlegen, für mehrere Erinnerungen.',
            [Locales.EsES]:
              'Escribe !10m o !1h30m en el nombre de la tarea al crearla, para varios recordatorios.',
          },
        },
      ],
    },
    {
      title: {
        [Locales.EnUS]: 'Other improvements and fixes',
        [Locales.NlNL]: 'Overige verbeteringen en fixes',
        [Locales.DeDE]: 'Weitere Verbesserungen und Fixes',
        [Locales.EsES]: 'Otras mejoras y correcciones',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'Duration filter',
            [Locales.NlNL]: 'Filter op duur',
            [Locales.DeDE]: 'Filter nach Dauer',
            [Locales.EsES]: 'Filtro de duración',
          },
          description: {
            [Locales.EnUS]:
              'Filter on "No duration" to find tasks missing a time estimate.',
            [Locales.NlNL]:
              'Filter op "Geen duur" om taken zonder tijdsinschatting te vinden.',
            [Locales.DeDE]:
              'Filter nach "Ohne Dauer" um Aufgaben ohne Zeitschätzung zu finden.',
            [Locales.EsES]:
              'Filtra por "Sin duración" para encontrar tareas sin estimación de tiempo.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'All-day order',
            [Locales.NlNL]: 'Volgorde hele dag',
            [Locales.DeDE]: 'Reihenfolge ganztägig',
            [Locales.EsES]: 'Orden todo el día',
          },
          description: {
            [Locales.EnUS]:
              "Completed tasks drop to the bottom of the calendar's all-day section.",
            [Locales.NlNL]:
              'Voltooide taken zakken naar onderen in het gedeelte voor de hele dag.',
            [Locales.DeDE]:
              'Erledigte Aufgaben rutschen im Ganztags-Bereich des Kalenders nach unten.',
            [Locales.EsES]:
              'Las tareas completadas bajan al final de la sección de todo el día del calendario.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Search in Trash',
            [Locales.NlNL]: 'Zoeken in Prullenbak',
            [Locales.DeDE]: 'Suche im Papierkorb',
            [Locales.EsES]: 'Buscar en la Papelera',
          },
          description: {
            [Locales.EnUS]:
              'Find one deleted task in a full Trash by typing its name.',
            [Locales.NlNL]:
              'Vind één verwijderde taak in een volle Prullenbak door de naam in te typen.',
            [Locales.DeDE]:
              'Finde eine gelöschte Aufgabe im vollen Papierkorb, indem du ihren Namen eintippst.',
            [Locales.EsES]:
              'Encuentra una tarea borrada en una Papelera llena escribiendo su nombre.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Goal progress',
            [Locales.NlNL]: 'Doelvoortgang',
            [Locales.DeDE]: 'Zielfortschritt',
            [Locales.EsES]: 'Progreso del objetivo',
          },
          description: {
            [Locales.EnUS]:
              'A goal completed at 72% now reads 72% everywhere instead of rounding up to 100%.',
            [Locales.NlNL]:
              'Een doel dat op 72% klaar is, leest nu overal 72% in plaats van afgerond 100%.',
            [Locales.DeDE]:
              'Ein zu 72% erreichtes Ziel zeigt jetzt überall 72% statt aufgerundet 100%.',
            [Locales.EsES]:
              'Un objetivo completado al 72% ahora muestra 72% en todas partes, no 100% redondeado.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Sort by date',
            [Locales.NlNL]: 'Sorteren op datum',
            [Locales.DeDE]: 'Sortieren nach Datum',
            [Locales.EsES]: 'Ordenar por fecha',
          },
          description: {
            [Locales.EnUS]:
              'Tasks with a time now sort above all-day tasks within the same day.',
            [Locales.NlNL]:
              'Taken met een tijd staan nu boven taken voor de hele dag binnen dezelfde dag.',
            [Locales.DeDE]:
              'Aufgaben mit Uhrzeit stehen jetzt über ganztägigen Aufgaben am selben Tag.',
            [Locales.EsES]:
              'Las tareas con hora van ahora por encima de las de todo el día dentro del mismo día.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Habit checkboxes',
            [Locales.NlNL]: 'Vinkjes bij gewoontes',
            [Locales.DeDE]: 'Gewohnheits-Häkchen',
            [Locales.EsES]: 'Casillas de hábitos',
          },
          description: {
            [Locales.EnUS]:
              "Habits show their checkbox on the calendar on the day they're due.",
            [Locales.NlNL]:
              'Gewoontes tonen hun vinkje in de agenda op de dag dat ze aan de beurt zijn.',
            [Locales.DeDE]:
              'Gewohnheiten zeigen ihr Häkchen im Kalender an dem Tag, an dem sie anstehen.',
            [Locales.EsES]:
              'Los hábitos muestran su casilla en el calendario el día que toca.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Habit progress',
            [Locales.NlNL]: 'Voortgang van gewoontes',
            [Locales.DeDE]: 'Gewohnheitsfortschritt',
            [Locales.EsES]: 'Progreso de hábitos',
          },
          description: {
            [Locales.EnUS]:
              "Habit progress on the calendar now only shows up when there's a completion behind it.",
            [Locales.NlNL]:
              'Voortgang van een gewoonte in de agenda verschijnt nu alleen als er een afvinking achter zit.',
            [Locales.DeDE]:
              'Der Fortschritt einer Gewohnheit im Kalender erscheint nur noch, wenn wirklich etwas erledigt ist.',
            [Locales.EsES]:
              'El progreso de un hábito en el calendario solo aparece cuando hay una marca detrás.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Quick add dates',
            [Locales.NlNL]: 'Datumwoorden',
            [Locales.DeDE]: 'Datumswörter',
            [Locales.EsES]: 'Fechas al añadir',
          },
          description: {
            [Locales.EnUS]:
              'Quick add now recognizes date words in your app language instead of English only.',
            [Locales.NlNL]:
              'Snel toevoegen herkent datumwoorden nu in de taal van je app, niet alleen in het Engels.',
            [Locales.DeDE]:
              'Schnell hinzufügen erkennt Datumswörter jetzt in deiner App-Sprache, nicht nur auf Englisch.',
            [Locales.EsES]:
              'Añadir rápido reconoce palabras de fecha en el idioma de la app, no solo en inglés.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Habits on Completed',
            [Locales.NlNL]: 'Gewoontes op Voltooid',
            [Locales.DeDE]: 'Seite Erledigt',
            [Locales.EsES]: 'Hábitos en Completadas',
          },
          description: {
            [Locales.EnUS]:
              'Hiding habits on the Completed page sticks when you reopen it.',
            [Locales.NlNL]:
              'Gewoontes verbergen op de pagina Voltooid blijft staan als je die opnieuw opent.',
            [Locales.DeDE]:
              'Gewohnheiten unter Erledigt auszublenden bleibt auch nach dem Neuöffnen.',
            [Locales.EsES]:
              'Ocultar los hábitos en la página Completadas se mantiene al volver a abrirla.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'All-day drag',
            [Locales.NlNL]: 'Slepen uit hele dag',
            [Locales.DeDE]: 'Ganztägig ziehen',
            [Locales.EsES]: 'Arrastrar todo el día',
          },
          description: {
            [Locales.EnUS]:
              'Dragging a task out of the all-day section keeps the duration you already set.',
            [Locales.NlNL]:
              'Een taak uit het gedeelte voor de hele dag slepen behoudt de duur die je al had ingesteld.',
            [Locales.DeDE]:
              'Eine Aufgabe aus dem Ganztags-Bereich zu ziehen behält die Dauer, die du gesetzt hast.',
            [Locales.EsES]:
              'Sacar una tarea de la sección de todo el día conserva la duración que ya habías puesto.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Tag and goal popups',
            [Locales.NlNL]: 'Label- en doelvenster',
            [Locales.DeDE]: 'Tag- und Ziel-Popup',
            [Locales.EsES]: 'Etiquetas y objetivos',
          },
          description: {
            [Locales.EnUS]:
              'The search field is focused when you open the tag or goal popup with T or G.',
            [Locales.NlNL]:
              'Het zoekveld staat actief als je het label- of doelvenster opent met T of G.',
            [Locales.DeDE]:
              'Das Suchfeld ist aktiv, wenn du das Tag- oder Ziel-Popup mit T oder G öffnest.',
            [Locales.EsES]:
              'El campo de búsqueda se activa al abrir la ventana de etiqueta u objetivo con T o G.',
          },
        },
      ],
    },
  ],
};
